import { useEffect, useRef, useState } from "react";

/**
 * Custom hook for verifying if a resource exists with built-in 
 * rate limiting, timeout handling, and validation
 */
function useResourceVerifier({
	id,                      // Resource ID to check
	validateIdFn,            // Function to validate ID format
	fetchResourceFn,         // Function that returns API call promise
	validateResponseFn,      // Function to validate response
	authHeader,              // Auth header for API calls
	timeoutMs = 5000,
	maxAttempts = 5,
	rateLimitMs = 1000,
	invalidIdMsg = "Invalid ID format",
	timeoutMsg = "Timeout. Resource may not exist or has been removed."
}) {
	const [isLoading, setIsLoading] = useState(true);
	const [resourceError, setResourceError] = useState(null);
	const [resourceData, setResourceData] = useState(null);
	
	// Refs for preventing infinite loops and managing timeouts
	const checkedRef = useRef(false);
	const requestAttempts = useRef(0);
	const lastRequestTime = useRef(0);
	const timeoutRef = useRef(null);

	/**
	 * Effect to check if resource exists if we have an ID
	 */
	useEffect(() => {
		if (checkedRef.current) {
			return;
		}

		if (!id || !validateIdFn(id)) {
			setResourceError(new Error(invalidIdMsg));
			setIsLoading(false);
			checkedRef.current = true;
			return;
		}

		const checkResourceExists = async () => {
			
			// Enforce rate limiting
			const now = Date.now();
			const timeSinceLastRequest = now - lastRequestTime.current;
			if (timeSinceLastRequest < rateLimitMs) {
				await new Promise(resolve => setTimeout(resolve, rateLimitMs - timeSinceLastRequest));
			}

			lastRequestTime.current = Date.now();
			requestAttempts.current++;

			try {
				const response = await fetchResourceFn();
				if (!validateResponseFn(response)) {
					throw new Error("Resource not found or invalid data received");
				}
				
				setResourceData(response.data);
				setIsLoading(false);
				checkedRef.current = true;
				
				if (timeoutRef.current) {
					clearTimeout(timeoutRef.current);
				}
			} catch (error) {
				
				if (requestAttempts.current >= maxAttempts) {
					setResourceError(error.response?.status === 404 
						? new Error("Resource not found") 
						: new Error(error.message || "Error loading resource data"));
					setIsLoading(false);
					checkedRef.current = true;
					return;
				}

				setTimeout(checkResourceExists, rateLimitMs);
			}
		};

		timeoutRef.current = setTimeout(() => {
			// Don't check isLoading directly to avoid stale closures
			// Only set error if we haven't completed processing yet
			if (!checkedRef.current) {
				setResourceError(new Error(timeoutMsg));
				setIsLoading(false);
				checkedRef.current = true;
			}
		}, timeoutMs);

		if (authHeader && Object.keys(authHeader).length > 0) {
			checkResourceExists();
		}

		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, [id, authHeader, validateIdFn, fetchResourceFn, validateResponseFn, invalidIdMsg, timeoutMsg, maxAttempts, rateLimitMs, timeoutMs]);

	return { isLoading, resourceError, resourceData };
}

export default useResourceVerifier;